import { LitElement, html, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzEnumFilterWebComponent } from '../../../ui/gzEnumFilter';
import { ObjectTypeOption, ModulesOption, ExclusionItemFormEvents, TooltipTexts } from '../types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ExclusionItemWildcardValidator, ExclusionValidator } from '../../../ui/gzForm/validators/exclusion.validator';
import { GzIconButtonWebComponent } from '../../../ui/gzIconButton';
import { InputWidthValues } from '../../../ui/gzInput';
import { ObjectType } from '../../exclusionItemGrid/types';
import { translateFn } from '../../../utils/translator.helper';
import { ExclusionItem } from '../../exclusionsPanel/types';

export class ExclusionItemFormWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @property({ type: String, attribute: true, reflect: true })
  objectTypeLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  objectTypePlaceholder = '';

  @property({ type: Number, attribute: true, reflect: true })
  objectTypeValue: number;

  @property({ type: Array, attribute: true, reflect: true })
  objectTypeOptions: Array<ObjectTypeOption> = [];

  @property({ type: String, attribute: true, reflect: true })
  excludedItemLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  excludedItemPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesLabelWidth = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesSearchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesConfirmActionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesNoSearchResultsString = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesListTitle = '';

  @property({ type: String, attribute: true, reflect: true })
  modulesAllItemsLabel = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  modulesCanSelectAll = false;

  @property({ type: Array, attribute: true, reflect: true })
  modulesOptions: Array<ModulesOption> = [];

  @property({ type: String, attribute: true, reflect: true })
  remarksLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  remarksPlaceholder = '';

  @property({ type: Array, attribute: true, reflect: true })
  tooltipTexts: Array<TooltipTexts> = [];

  @query('#objectTypeDropDown')
  objectTypeDropDown!: GzFormFieldWebComponent;

  @query('#excludedItemInput')
  excludedItemInput!: GzFormFieldWebComponent;

  @query('#modulesDropDown')
  modulesDropDown!: GzEnumFilterWebComponent;

  @query('#remarks')
  remarksInput!: GzFormFieldWebComponent;

  @query('#addButton')
  addButton: GzButtonWebComponent;

  @query('#tooltip')
  tooltip: GzIconButtonWebComponent;

  @state()
  formGroup: FormGroup | null;

  @state()
  private subscription: Subscription | null;

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.createFormGroup();
  }

  updated(properties: PropertyValues): void {
    if (properties.has('objectTypeValue')) {
      this.objectTypeDropDown.Input.setFormValue(this.objectTypeValue);
    }
  }

  render() {
    return html`
      <div class="exclusion-form">
        <div class="exclusion-form--row d-flex flex-row align-items-center">
          <div class="exclusion-form--field">
            <gz-form-field id="objectTypeDropDown" disableVerticalMargin required>
              <gz-dropdown
                @selectionChanged="${this.handleObjectTypeChange}"
                gz-form-input
                label="${this.objectTypeLabel}"
                placeholder="${this.objectTypePlaceholder}"
                options="${JSON.stringify(this.objectTypeOptions)}"
              ></gz-dropdown>
            </gz-form-field>
          </div>

          <div>
            <gz-icon-button
              icon="ds-icon-notification_info"
              id="tooltip"
              size="32"
              fontSize="20"
              buttonType="circle"
              buttonFill="quiet"
              buttonColor="secondary"
              style="margin: 0 10px 0 -15px;"
            ></gz-icon-button>
          </div>

          <div class="exclusion-form--field excluded-item-input">
            <gz-form-field id="excludedItemInput" disableVerticalMargin required>
              <gz-input
                gz-form-input
                inputWidth=${InputWidthValues.XLARGE}
                label="${this.excludedItemLabel}"
                placeholder="${this.excludedItemPlaceholder}"
                @change="${this.handleExcludedItemChange}"
              ></gz-input>
            </gz-form-field>
          </div>

          <div class="exclusion-form--field">
            <gz-enum-filter
              id="modulesDropDown"
              label="${this.modulesLabel}"
              filterOptions="${JSON.stringify(this.modulesOptions)}"
              labelWidth=${InputWidthValues.LARGE}
              searchPlaceholderString="${this.modulesSearchPlaceholderString}"
              confirmActionButtonString="${this.modulesConfirmActionButtonString}"
              noSearchResultsString="${this.modulesNoSearchResultsString}"
              listTitle="${this.modulesListTitle}"
              allItemsLabel="${this.modulesAllItemsLabel}"
              canSelectAll="${this.modulesCanSelectAll}"
              @selectionChange="${this.handleModuleChange}"
            ></gz-enum-filter>
          </div>

          <div class="exclusion-form--field">
            <gz-form-field disableVerticalMargin id="remarks">
              <gz-input
                gz-form-input
                inputWidth=${InputWidthValues.LARGE}
                label="${this.remarksLabel}"
                placeholder="${this.remarksPlaceholder}"
              ></gz-input>
            </gz-form-field>
          </div>

          <div class="exclusion-form--actions">
            <gz-icon-button
              id="addButton"
              size="32"
              fontSize="20"
              buttonType="square"
              icon="ds-icon-add"
              buttonFill="primary"
              @click="${this.addNewExclusionItem}"
            >
            </gz-icon-button>
          </div>
        </div>
      </div>
    `;
  }

  addNewExclusionItem(): void {
    this.dispatchEvent(
      new CustomEvent(ExclusionItemFormEvents.CREATE_NEW_EXCLUSION_ITEM, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleObjectTypeChange(e: WebComponentEvent): void {
    // map the current selected value of the dropdown to the tooltip text
    const tooltipObj = this.tooltipTexts.find(obj => obj.value === e.detail.value);

    this.tooltip.tooltipText = tooltipObj?.text || '';

    this.dispatchEvent(
      new CustomEvent(ExclusionItemFormEvents.CHANGE_OBJECT_TYPE, {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );

    this.modulesDropDown.setAttribute('filterOptions', JSON.stringify(this.modulesOptions));
    const exclusionItem = this.getExclusionItem();
    this.excludedItemInput.Validators = [RequiredValidator(''), ExclusionValidator(e.detail.value, ''), ExclusionItemWildcardValidator(exclusionItem, translateFn('You cannot use wildcards'))];
  }

  handleExcludedItemChange(): void {
    const exclusionItem = this.getExclusionItem();
    this.excludedItemInput.Validators = [RequiredValidator(''), ExclusionValidator(exclusionItem.type as ObjectType, ''), ExclusionItemWildcardValidator(exclusionItem, translateFn('You cannot use wildcards'))];
  }

  handleModuleChange(): void {
    const exclusionItem = this.getExclusionItem();
    this.excludedItemInput.Validators = [RequiredValidator(''), ExclusionValidator(exclusionItem.type as ObjectType, ''), ExclusionItemWildcardValidator(exclusionItem, translateFn('You cannot use wildcards'))];
  }

  public getExclusionItem(): ExclusionItem {
    return {
      type: this.objectTypeDropDown.Input.getFormValue(),
      path: this.excludedItemInput.Input.getFormValue(),
      modules: this.modulesDropDown.getFormValue() as Array<number>,
    };
  }

  disconnectedCallback(): void {
    this.formGroup?.unsubscribe();
    this.subscription?.unsubscribe();
    super.disconnectedCallback();
  }

  private createFormGroup() {
    this.objectTypeDropDown.Validators = [RequiredValidator('')];
    this.excludedItemInput.Validators = [RequiredValidator('')];

    this.formGroup = new FormGroup({
      objectType: this.objectTypeDropDown,
      excludedItem: this.excludedItemInput,
    });

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.addButton.disabled = !valid;
      },
    });
  }
}
